import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

// ===================== GoodsRecognition (оприходование излишков) =====================

export interface GoodsRecognitionItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  /** 0415: склад рядка (порожньо → шапка/успадкування). */
  warehouse_id?: string | null;
  characteristic_id?: string | null;
  series_id?: string | null;
  quantity: string;
  unit_cost: string;
  amount?: string;
}

export interface GoodsRecognitionListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string | null;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface GoodsRecognition extends GoodsRecognitionListItem {
  reason: string | null;
  posted_at: string | null;
  comment: string | null;
  items: GoodsRecognitionItem[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface GoodsRecognitionCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id?: string | null;
  reason?: string | null;
  comment?: string | null;
  items: GoodsRecognitionItem[];
}

export type GoodsRecognitionUpdate = Partial<GoodsRecognitionCreate>;

export const goodsRecognitions = {
  list: (params?: {
    organization_id?: string; warehouse_id?: string;
    posted?: boolean; limit?: number; offset?: number;
    date_from?: string; date_to?: string;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
  }) =>
    api.get<Page<GoodsRecognitionListItem>>('/goods-recognitions', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<GoodsRecognition>(`/goods-recognitions/${id}`).then((r) => r.data),
  create: (payload: GoodsRecognitionCreate) =>
    api.post<GoodsRecognition>('/goods-recognitions', payload).then((r) => r.data),
  update: (id: string, payload: GoodsRecognitionUpdate) =>
    api.patch<GoodsRecognition>(`/goods-recognitions/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/goods-recognitions/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<GoodsRecognition>(`/goods-recognitions/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<GoodsRecognition>(`/goods-recognitions/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/goods-recognitions/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// ===================== GoodsWriteoff (списание недостач) =====================

export interface GoodsWriteoffItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  /** 0415: склад рядка (порожньо → шапка/успадкування). */
  warehouse_id?: string | null;
  characteristic_id?: string | null;
  series_id?: string | null;
  quantity: string;
  amount?: string;
  /** 2026-08-09 (вкладка «Рахунки обліку»): рахунок запасів (авто: 281). */
  account_code?: string | null;
}

export interface GoodsWriteoffListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string | null;
  writeoff_reason: string;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface GoodsWriteoff extends GoodsWriteoffListItem {
  reason_text: string | null;
  posted_at: string | null;
  comment: string | null;
  items: GoodsWriteoffItem[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface GoodsWriteoffCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id?: string | null;
  writeoff_reason?: string;
  reason_text?: string | null;
  comment?: string | null;
  items: GoodsWriteoffItem[];
}

export type GoodsWriteoffUpdate = Partial<GoodsWriteoffCreate>;

export interface WriteoffCostPreview {
  available: boolean;
  cost: string | null;
  method: string;
}

export const goodsWriteoffs = {
  /** Попередня собівартість рядка (ті самі партії/метод, без запису). */
  costPreview: (params: {
    organization_id: string; warehouse_id: string; nomenclature_id: string;
    quantity: string; on_date: string;
    characteristic_id?: string | null; series_id?: string | null;
  }) =>
    api.get<WriteoffCostPreview>('/goods-writeoffs/cost-preview', { params }).then((r) => r.data),
  list: (params?: {
    organization_id?: string; warehouse_id?: string;
    writeoff_reason?: string; posted?: boolean; limit?: number; offset?: number;
    date_from?: string; date_to?: string;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
  }) =>
    api.get<Page<GoodsWriteoffListItem>>('/goods-writeoffs', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<GoodsWriteoff>(`/goods-writeoffs/${id}`).then((r) => r.data),
  create: (payload: GoodsWriteoffCreate) =>
    api.post<GoodsWriteoff>('/goods-writeoffs', payload).then((r) => r.data),
  update: (id: string, payload: GoodsWriteoffUpdate) =>
    api.patch<GoodsWriteoff>(`/goods-writeoffs/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/goods-writeoffs/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<GoodsWriteoff>(`/goods-writeoffs/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<GoodsWriteoff>(`/goods-writeoffs/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/goods-writeoffs/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
